import React, { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import { map, prop, sum, path, isEmpty } from 'ramda';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { generate } from 'short-uuid';

import { useManagedMutation, useManagedQuery } from 'utils/managedHooks';
import { useDefaultedCurrencyFromURL } from 'utils/hooks/currency';
import { historyShape, userShape } from 'constants/propTypes';
import { providers as paymentProviders, codes, statusShortCode } from 'constants/payment';
import { Page, Row, Column } from 'components/shared/Layout';
import { ConfirmButton } from 'components/ui/Button';
import { GET_CUSTOMER_DETAILS } from 'store/queries';

import ConfirmBookingMutation from 'components/mutations/ConfirmBookingMutation';
import RevertExchangeButton from 'components/shared/RevertExchangeButton';
import ReviewBookingSummary from 'components/ui/ReviewBookingSummary';
import { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';
import AlertBox from 'components/ui/AlertBox';
import { FullPageSpinner } from 'admin-components/components';
import { hasHotel, loyaltyPointsDifferencesCreationAndCancelLegs, getBookingAttachedLoyaltyAccount } from 'utils/booking';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { getCarrier, getTravelClass } from 'pages/Exchange/Exchange.utils';
import ApolloErrorSectionCustom from 'components/errors/ApolloErrorSectionCustom/ApolloErrorSectionCustom';
import AgentPortalError from 'errors/AgentPortalError';
import ExchangeCost from './ExchangeCost';
import { bookingQuery } from './queries';
import { useUpgradeQuote } from './useUpgradeQuote';
import { SP, STD } from 'constants/classOfService';
import { subtract } from '../../../utils/calculator';
import { marginTariff } from 'constants/tariffCodes';
import { NOT_THIRD_PARTY_BOOKING } from 'types/booking';

import {
  ADD_NOTE_TO_BOOKING,
  ADD_PAYMENT,
  UPGRADE_WITH_POINTS,
  ROLLBACK_LOYALTY_ACTIVITY,
  REDEEM_TICKET_EXCHANGES
} from '../../../store/mutations';
import { getDepartureDateTime, getDestinationUIC, getOriginUIC, getRedeemTicketsData } from '../../../utils/exchange';
import Modal from '../../../components/ui/Modal';
import { POINTS_REDEMPTION_MISMATCH_KEY } from 'constants/clubEurostar';
import { isEurostarOrganization } from 'utils/user';

const ExchangeConfirmation = ({ history, user, isMultiCurrency, bookingCurrency, reference }) => {
  const [customerInfosFetching, setCustomerInfoFetching] = useState(false);
  const [customerFetchingError, setCustomerFetchingError] = useState({});
  const [redeemUpgradeWithPointsError, setRedeemUpgradeWithPointsError] = useState(false);
  const [addPaymentError, setAddPaymentError] = useState(false);
  const [rollbackActivityError, setRollbackActivityError] = useState(false);
  const [confirmationError, setConfirmationError] = useState(false);
  const [currency] = useDefaultedCurrencyFromURL({
    availableCurrencies: user.paymentCurrencies,
    defaultCurrency: bookingCurrency,
    isMultiCurrency
  });
  const apolloClient = useApolloClient();

  const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';

  const shouldUpgradeWithPoints = new URLSearchParams(history?.location?.search).get('isUpgradeWithPoints') === 'true';
  const isUpgradeWithPointsPath = shouldUpgradeWithPoints ? `&isUpgradeWithPoints=${shouldUpgradeWithPoints}` : '';

  const shouldFlowForward = new URLSearchParams(history?.location?.search).get('isFlowForward') === 'true';
  const isFlowForwardPath = shouldFlowForward ? `&isFlowForward=${shouldFlowForward}` : '';

  const shouldMissedDeparture = new URLSearchParams(history?.location?.search).get('isMissedDeparture') === 'true';
  const isMissedDeparturePath = shouldMissedDeparture ? `&isMissedDeparture=${shouldMissedDeparture}` : '';

  const shouldImpulseUpgrade = new URLSearchParams(history?.location?.search).get('isImpulseUpgrade') === 'true';
  const isImpulseUpgradePath = shouldImpulseUpgrade ? `&isImpulseUpgrade=${shouldImpulseUpgrade}` : '';

  const shouldWaivePoints = new URLSearchParams(history?.location?.search).get('waivePoints') === 'true';
  const isWaivePointsPath = shouldWaivePoints ? `&waivePoints=${shouldWaivePoints}` : '';

  const marginTrainsCodes = [marginTariff.MARGIN_TRAIN, marginTariff.MARGIN_TRAIN_ER];

  const { loading: bookingLoading, data } = useManagedQuery(bookingQuery, {
    variables: { reference, currency },
    fetchPolicy: 'network-only'
  });

  const [redeemTicketExchanges, { loading: redeemTicketExchangesLoading }] = useManagedMutation(REDEEM_TICKET_EXCHANGES);
  const [upgradeWithPoints, { loading: upgradeWithPointsLoading }] = useManagedMutation(UPGRADE_WITH_POINTS);

  const [addNote, { loading: addNoteLoading }] = useManagedMutation(ADD_NOTE_TO_BOOKING);

  const [addPayment, { loading: addPaymentLoading }] = useManagedMutation(ADD_PAYMENT);
  const [rollbackActivity, { loading: rollbackActivityLoading }] = useManagedMutation(ROLLBACK_LOYALTY_ACTIVITY);

  const {
    quote: upgradeQuote,
    error: upgradeQuoteError,
    loading: upgradeQuoteLoading
  } = useUpgradeQuote(shouldUpgradeWithPoints ? path(['booking'], data) : null);

  if (bookingLoading || upgradeQuoteLoading) {
    return <FullPageSpinner />;
  }

  const booking = path(['booking'], data);

  const {
    pendingCreationLegs,
    pendingCancellationLegs,
    totalPendingCreationFarePrice,
    totalPendingCancellationFarePrice,
    totalAddonPendingCancellationFarePrice,
    totalAddonPendingCreationFarePrice,
    totalPriceToBePaid,
    totalExchangeFee,
    provisionalOfflineFees,
    customer,
    activeFlowForwardFees,
    activeImpulseUpgradeFees,
    payments,
    isPackage,
    productFamilies,
    outbound,
    inbound,
    salesChannelCode
  } = booking;

  const bookingCreatedByThirdParty = !NOT_THIRD_PARTY_BOOKING.includes(salesChannelCode);
  const attachedMembershipNumber = getBookingAttachedLoyaltyAccount(booking);
  const isWrongAccountLoggedIn = attachedMembershipNumber && membershipNumber && membershipNumber !== attachedMembershipNumber;

  const isBookingLoyalty = productFamilies?.some((pf) => pf.familyGroup === productFamilyCodes.EUROSTAR_LOYALTY);

  const email = path(['email'], customer);
  const update = () => {
    history.push(`/manage/bookings/${reference}/journeys${membershipNumberUrl}`);
  };
  const refund = () => {
    history.push(`/manage/bookings/${reference}/refund-methods?currency=${currency}`);
  };
  const errRedirectUrl = `/exchange/${reference}/confirm${membershipNumberUrl}`;
  const noPendingCreationLegs = !(pendingCreationLegs && pendingCreationLegs[0]);
  const noPendingCancellationLegs = !(pendingCancellationLegs && pendingCancellationLegs[0]);

  if (noPendingCreationLegs && noPendingCancellationLegs) {
    return <Redirect to={`/manage/bookings/${reference}/journeys${membershipNumberUrl}`} />;
  }

  const shouldFetchCustomerInfos = isBookingLoyalty || shouldUpgradeWithPoints;
  const loyaltyPointsDifference = shouldFetchCustomerInfos ? loyaltyPointsDifferencesCreationAndCancelLegs(booking) : 0;
  const pointsForUpgradeWithPoints = shouldUpgradeWithPoints ? upgradeQuote?.points : null;

  const displayedLoyaltyPointsDifference = !shouldWaivePoints ? loyaltyPointsDifference : 0;

  const fareDifference = sum([
    Math.max(0, subtract(totalPendingCreationFarePrice, totalPendingCancellationFarePrice)),
    subtract(totalAddonPendingCreationFarePrice, totalAddonPendingCancellationFarePrice)
  ]);

  const fareDifferenceWithoutAddOns = Math.max(0, subtract(totalPendingCreationFarePrice, totalPendingCancellationFarePrice));

  const passengersForExtras = pendingCancellationLegs?.map((pcl) => pcl?.bookingItems?.map((ep) => ep?.passengerId))?.flat();

  const extrasCreated = sum(
    pendingCreationLegs?.map((pcl) =>
      pcl?.extraProducts?.filter((p) => !marginTrainsCodes.includes(p.code))?.reduce((acc, ep) => acc + ep?.price, 0)
    )
  );
  const trainMarginsCreated = sum(
    pendingCreationLegs?.map((pcl) =>
      pcl?.extraProducts?.filter((p) => marginTrainsCodes.includes(p.code))?.reduce((acc, ep) => acc + ep?.price, 0)
    )
  );

  const extrasCancelled = sum(
    pendingCancellationLegs?.map((pcl) =>
      pcl?.extraProducts
        ?.filter((ep) => !ep.isCancelled && passengersForExtras?.includes(ep?.passengerId) && !marginTrainsCodes.includes(ep.code))
        ?.reduce((acc, ep) => acc + ep?.price, 0)
    )
  );

  const trainMarginsCancelled = sum(
    pendingCancellationLegs?.map((pcl) =>
      pcl?.extraProducts
        ?.filter((p) => !p.isCancelled && marginTrainsCodes.includes(p.code) && passengersForExtras?.includes(p?.passengerId))
        ?.reduce((acc, ep) => acc + ep?.price, 0)
    )
  );

  const extrasDifference = subtract(extrasCreated, extrasCancelled);
  const trainMarginDifference = subtract(trainMarginsCreated, trainMarginsCancelled);

  const segments = (pendingCreationLegs || []).map((pl) => ({
    direction: pl.direction,
    departureDate: moment(pl.timing.isoDate).format('YYYY-MM-DD')
  }));

  const items = `bookingItems=${pendingCancellationLegs
    .map((leg) => leg.bookingItems)
    .reduce((refs, bookingItems) => refs.concat(bookingItems.map((b) => b.itemRef)), [])}`;
  const dates = segments.map(({ direction, departureDate }) => `${direction}Date=${departureDate}`).join('&');

  const errorRedirect = `/exchange/${reference}?${items}&${dates}${membershipNumber ? `&membershipNumber=${membershipNumber}` : ''}`;
  const totalOfflineFees = sum(map(prop('price'), provisionalOfflineFees));
  const totalFlowForwardFees = sum(activeFlowForwardFees.map((f) => f.price));
  const totalImpulseUpgradeFees = sum(activeImpulseUpgradeFees.map((f) => f.price));

  const hasNonEurostarLegs =
    outbound?.length > 0 || inbound?.length > 0
      ? [...outbound, ...inbound].some(({ isSummatedFare, serviceType }) => isSummatedFare && !EUROSTAR_CARRIERS.includes(serviceType.code))
      : false;

  const nonEurostarCreationLegsPrice =
    pendingCreationLegs?.length > 0
      ? pendingCreationLegs.reduce((acc, { isSummatedFare, serviceType, bookingItems }) => {
          if (isSummatedFare && !EUROSTAR_CARRIERS.includes(serviceType.code)) {
            return acc + bookingItems.reduce((sum, { price }) => sum + price, 0);
          }

          return acc;
        }, 0)
      : 0;
  const nonEurostarCancellationLegsPrice =
    pendingCreationLegs?.length > 0
      ? pendingCancellationLegs.reduce((acc, { isSummatedFare, serviceType, bookingItems }) => {
          if (isSummatedFare && !EUROSTAR_CARRIERS.includes(serviceType.code)) {
            return acc + bookingItems.reduce((sum, { price }) => sum + price, 0);
          }

          return acc;
        }, 0)
      : 0;
  const nonEurostarExchangeFeesPrice =
    pendingCreationLegs?.length > 0
      ? pendingCancellationLegs.reduce((acc, { isSummatedFare, serviceType, fees }) => {
          if (isSummatedFare && !EUROSTAR_CARRIERS.includes(serviceType.code)) {
            return (
              acc +
              fees.reduce((sum, { price, cancelled }) => {
                if (!cancelled) {
                  return sum + price;
                }

                return sum;
              }, 0)
            );
          }

          return acc;
        }, 0)
      : 0;

  const isNonEurostarLegsFareDifferenceOnly =
    (nonEurostarCreationLegsPrice - nonEurostarCancellationLegsPrice).toFixed(2) ===
    (totalPendingCreationFarePrice - totalPendingCancellationFarePrice).toFixed(2);
  const isNonEurostarLegsExchangeFeeOnly = nonEurostarExchangeFeesPrice.toFixed(2) === totalExchangeFee.toFixed(2);

  const toggleRedeemUpradeWithPointsModal = () => setRedeemUpgradeWithPointsError(false);
  const toggleAddPaymentErrorModal = () => setAddPaymentError(false);
  const toggleRollbackActivityErrorModal = () => setRollbackActivityError(false);
  const toggleConfirmationErrorModal = () => {
    setConfirmationError(false);
    history.push(
      // eslint-disable-next-line max-len
      `/exchange/${reference}/payment?currency=${currency}${isUpgradeWithPointsPath}${isFlowForwardPath}${isMissedDeparturePath}${isImpulseUpgradePath}${
        membershipNumber ? `&membershipNumber=${membershipNumber}` : ''
      }`
    );
  };

  const handleExchange = async (shouldConfirm = false, confirmMutation = () => {}) => {
    let activityId = null;

    if (shouldFetchCustomerInfos) {
      if (isWrongAccountLoggedIn) {
        setCustomerFetchingError(new Error('Booking is not linked to this loyalty account'));

        return;
      }
      try {
        setCustomerInfoFetching(true);
        await apolloClient.query({
          query: GET_CUSTOMER_DETAILS,
          variables: {
            membershipNumber
          }
        });
      } catch (err) {
        setCustomerFetchingError(err);

        return;
      } finally {
        setCustomerInfoFetching(false);
      }
    }

    if (shouldUpgradeWithPoints && pointsForUpgradeWithPoints > 0) {
      let totalPointsSpent = null;

      try {
        const {
          data: {
            upgradeWithPoints: { activityId: actId, pointsSpent }
          }
        } = await upgradeWithPoints({
          variables: {
            bookingReference: reference,
            membershipNumber,
            carrier: upgradeQuote.carrier,
            journeyType: upgradeQuote.journeyType,
            origin: getOriginUIC(booking),
            destination: getDestinationUIC(booking),
            departureDateTime: getDepartureDateTime(booking),
            fromTravelClass: STD,
            toTravelClass: SP,
            fromTravelClassOfService: getTravelClass({ legs: booking.pendingCancellationLegs }),
            toTravelClassOfService: upgradeQuote.toTravelClassOfService,
            numberOfPassengers: upgradeQuote.numberOfPassengers,
            legsPerPassenger: upgradeQuote.legsPerPassenger,
            upgradeJourneys: upgradeQuote.upgradeJourneys,
            useNewLoyaltySystem: true,
            actionBy: user.username
          }
        });

        await addNote({
          variables: {
            note: `Upgrade of cash tickets for Club Eurostar account: ${membershipNumber}, totalPointsRedeemed: ${pointsSpent}`,
            bookingReference: reference
          }
        });

        activityId = actId;
        totalPointsSpent = pointsSpent;
      } catch (err) {
        setRedeemUpgradeWithPointsError(true);

        return;
      }

      try {
        await addPayment({
          variables: {
            bookingReference: reference,
            payments: [
              {
                amount: Math.abs(fareDifferenceWithoutAddOns),
                currency,
                provider: paymentProviders.NONE,
                method: codes.LOYALTY_POINTS,
                status: statusShortCode.success,
                reference: `${totalPointsSpent}-${membershipNumber ? membershipNumber + '-' : ''}${generate()}`,
                externalReference: activityId
              }
            ]
          }
        });
      } catch (err) {
        setAddPaymentError(true);

        try {
          await rollbackActivity({
            variables: {
              activityId,
              useNewLoyaltySystem: true,
              actionBy: user.username
            }
          });
        } catch (error) {
          setRollbackActivityError(true);
        }

        return;
      }
    }

    if (!shouldUpgradeWithPoints && isBookingLoyalty && shouldConfirm && displayedLoyaltyPointsDifference > 0) {
      const carrier = getCarrier(booking);
      const result = await redeemTicketExchanges({
        variables: {
          membershipNumber,
          bookingReference: reference,
          carrier,
          redeemTickets: getRedeemTicketsData(booking),
          actionBy: user.username
        }
      });

      if (result.data?.redeemTicketExchanges) {
        activityId = result.data.redeemTicketExchanges.transactionJournalId;

        const totalPointsCost = Math.abs(result.data.redeemTicketExchanges.totalPointsCost);

        await addNote({
          variables: {
            note: `Exchange of tickets for Club Eurostar account: ${membershipNumber}, totalPointsRedeemed: ${totalPointsCost}`,
            bookingReference: reference
          }
        });

        if (totalPointsCost !== displayedLoyaltyPointsDifference) {
          // points redemption mismatch - collison might have redeemed wrong amount
          // store the mismatch in localStorage for it to be checked on journeys tab to notify agent
          localStorage.setItem(
            POINTS_REDEMPTION_MISMATCH_KEY,
            JSON.stringify({
              reference: reference,
              membershipNumber,
              expected: displayedLoyaltyPointsDifference,
              redeemed: totalPointsCost
            })
          );
        }
      }
    }

    if (
      !shouldUpgradeWithPoints &&
      isBookingLoyalty &&
      shouldConfirm &&
      displayedLoyaltyPointsDifference === 0 &&
      loyaltyPointsDifference > 0
    ) {
      await addNote({
        variables: {
          note: `Points waived, totalPointsWaived: ${Math.abs(loyaltyPointsDifference)}`,
          bookingReference: reference
        }
      });
    }

    if (!shouldConfirm) {
      history.push(
        // eslint-disable-next-line max-len
        `/exchange/${reference}/payment?currency=${currency}${isUpgradeWithPointsPath}${isFlowForwardPath}${isMissedDeparturePath}${isImpulseUpgradePath}${isWaivePointsPath}${
          membershipNumber ? `&membershipNumber=${membershipNumber}` : ''
        }`
      );
    }

    try {
      await confirmMutation({
        variables: {
          bookingReference: reference,
          email,
          sendConfirmationEmail: !booking.isGroupBooking
        }
      });
    } catch (err) {
      setConfirmationError(true);

      if (isBookingLoyalty && loyaltyPointsDifference > 0 && activityId) {
        try {
          await rollbackActivity({
            variables: {
              activityId,
              useNewLoyaltySystem: true,
              actionBy: user.username
            }
          });
        } catch (error) {
          setRollbackActivityError(true);
        }
      }
    }
  };

  if (upgradeQuoteError) {
    return (
      <ApolloErrorSectionCustom error={new AgentPortalError(upgradeQuoteError)}>
        <RevertExchangeButton reference={reference} currency={currency} payments={payments} hasHotel={isPackage || hasHotel(booking)} />
      </ApolloErrorSectionCustom>
    );
  }

  if (!isEmpty(customerFetchingError)) {
    return (
      <ApolloErrorSectionCustom error={customerFetchingError}>
        <RevertExchangeButton
          reference={reference}
          currency={currency}
          payments={payments}
          hasHotel={isPackage || hasHotel(booking)}
          shouldRedirectToClubEurostar
          {...(isWrongAccountLoggedIn && {
            ctaText: 'exchangeConfirmation.restoreBookingClubEurostarWrongAccount'
          })}
        />
      </ApolloErrorSectionCustom>
    );
  }

  if (redeemUpgradeWithPointsError) {
    return (
      <Modal isOpen={redeemUpgradeWithPointsError} onClose={toggleRedeemUpradeWithPointsModal} data-id="modal-redeem-failed">
        <FormattedMessage id="upgradePoints.redeemFailed" />
      </Modal>
    );
  }

  if (addPaymentError) {
    return (
      <Modal isOpen={addPaymentError} onClose={toggleAddPaymentErrorModal} data-id="modal-add-payment-failed">
        <FormattedMessage id="upgradePoints.addPaymentFailed" />
      </Modal>
    );
  }

  if (rollbackActivityError) {
    return (
      <Modal isOpen={rollbackActivityError} onClose={toggleRollbackActivityErrorModal} data-id="modal-rollback-activity-failed">
        <FormattedMessage id="upgradePoints.rollbackActivityFailed" />
      </Modal>
    );
  }

  if (confirmationError) {
    return (
      <Modal isOpen={confirmationError} onClose={toggleConfirmationErrorModal} data-id="modal-confirmation-failed">
        <FormattedMessage id="upgradePoints.confirmationFailed" />
      </Modal>
    );
  }

  const getConfirmButtonLabel = () => {
    if (totalPriceToBePaid > 0) {
      return 'exchangeConfirmation.takePayment';
    }

    if (totalPriceToBePaid < 0) {
      return 'exchanges.makeRefund';
    }

    return 'exchanges.confirm';
  };

  return (
    <ConfirmBookingMutation update={totalPriceToBePaid < 0 ? refund : update} errRedirectUrl={errRedirectUrl}>
      {({ confirmBooking, loading }) => (
        <Page>
          <Row start={2} span={14}>
            <h1>
              <FormattedMessage id="exchangeConfirmation.reviewExchange" />
            </h1>
            <ReviewBookingSummary
              title="exchangeConfirmation.bookingSummary"
              currency={currency}
              booking={booking}
              fareDifference={fareDifference}
              fareDifferenceWithoutAddOns={fareDifferenceWithoutAddOns}
              isExchangePath
              showToggle
            />
            <ExchangeCost
              currency={currency}
              flowForwardFee={totalFlowForwardFees}
              impulseUpgradeFee={totalImpulseUpgradeFees}
              totalPrice={totalPriceToBePaid}
              offlineFee={totalOfflineFees}
              exchangeFee={totalExchangeFee}
              oldBookingFare={totalPendingCancellationFarePrice}
              newBookingFare={totalPendingCreationFarePrice}
              oldAdditionalProductsFarePrice={totalAddonPendingCancellationFarePrice}
              newAdditionalProductsFarePrice={totalAddonPendingCreationFarePrice}
              bookingReference={reference}
              canOverrideFees={user.canOverrideFees}
              errorRedirect={errorRedirect}
              loyaltyPointsDifference={displayedLoyaltyPointsDifference}
              pointsForUpgradeWithPoints={pointsForUpgradeWithPoints}
              extrasDifference={extrasDifference}
              pendingCreationLegs={pendingCreationLegs}
              pendingCancellationLegs={pendingCancellationLegs}
              user={user}
              trainMarginDifference={trainMarginDifference}
              isLoyalty={pointsForUpgradeWithPoints > 0 || loyaltyPointsDifference > 0}
              hasNonEurostarLegs={hasNonEurostarLegs}
              isNonEurostarLegsFareDifferenceOnly={isNonEurostarLegsFareDifferenceOnly}
              isNonEurostarLegsExchangeFeeOnly={isNonEurostarLegsExchangeFeeOnly}
            />
          </Row>
          {isEurostarOrganization(user) && bookingCreatedByThirdParty && totalPriceToBePaid < 0 && (
            <Row span={14} start={2}>
              <AlertBox variant={AlertBox.variants.WARNING}>
                <FormattedMessage id="journeySummary.thirdPartyBooking" />
              </AlertBox>
            </Row>
          )}
          <Row span={14} start={2}>
            <Column span={4}>
              {(shouldUpgradeWithPoints && totalPriceToBePaid - fareDifferenceWithoutAddOns > 0) ||
              (!shouldUpgradeWithPoints && totalPriceToBePaid > 0) ? (
                <ConfirmButton
                  data-cy="confirm-button"
                  onClick={() => handleExchange()}
                  loading={customerInfosFetching}
                  disabled={customerInfosFetching}
                  variant="primary"
                >
                  <FormattedMessage id="exchangeConfirmation.takePayment" />
                </ConfirmButton>
              ) : (
                <ConfirmButton
                  data-cy="confirm-button"
                  loading={
                    loading ||
                    customerInfosFetching ||
                    redeemTicketExchangesLoading ||
                    upgradeWithPointsLoading ||
                    addNoteLoading ||
                    addPaymentLoading ||
                    rollbackActivityLoading
                  }
                  disabled={
                    loading ||
                    customerInfosFetching ||
                    redeemTicketExchangesLoading ||
                    upgradeWithPointsLoading ||
                    addNoteLoading ||
                    addPaymentLoading ||
                    rollbackActivityLoading
                  }
                  onClick={() => handleExchange(true, confirmBooking)}
                >
                  <FormattedMessage id={getConfirmButtonLabel()} />
                </ConfirmButton>
              )}
            </Column>
            <Column span={4}>
              <RevertExchangeButton
                reference={reference}
                currency={currency}
                payments={payments}
                hasHotel={isPackage || hasHotel(booking)}
              />
            </Column>
          </Row>
        </Page>
      )}
    </ConfirmBookingMutation>
  );
};

ExchangeConfirmation.propTypes = {
  history: historyShape,
  user: userShape,
  isMultiCurrency: PropTypes.bool.isRequired,
  bookingCurrency: PropTypes.string.isRequired,
  reference: PropTypes.string.isRequired
};

export default ExchangeConfirmation;
